/**
 * Screen codes (beta builds only, Matt 2026-10-19; docs/specs/beta-feedback-aids-2026-10-19.md): every screen has a short
 * code (S01, S02, … one sequence, no group letters) plus a state suffix, shown as a small badge and in the report link's
 * accessible name, so a tester can quote the exact screen (the tester guide asks for it). The "Report a problem on this page"
 * email never carries it (Matt 2026-10-22 19:50; src/beta/report.ts). The ONE place codes are defined: a new screen (flow
 * step, information page, dialog) must get one here, or src/beta/screenCodes.test.ts fails. Production builds keep only the screen code in the build stripe (src/beta/stripeCode.ts) and strip the rest of the beta aids
 * (vite.config.ts `__BETA__`; tests/ui/beta.browser.test.ts checks the built files). Listed with a screenshot each
 * in docs/review/screen-codes.md.
 */

/**
 * Every screen and its code: ONE plain sequence, S01, S02, … (Matt 2026-10-22). No letter or number range may reveal a group
 * (a tester's report quotes the code, and the code must not hint at who the tester is): the flow screens run in flow order, the
 * four flows' screens interleaved step by step in a mixed order, then the pages and dialogs. The mapping lives only here.
 */
export const SCREEN_CODES = {
  start: 'S01',
  who: 'S02',
  upload: 'S03',
  uploadForm106: 'S04',
  manualEntry: 'S05',
  form106Entry: 'S06',
  scanReview: 'S07',
  payslipQuestions: 'S08',
  foreignQuestions: 'S09',
  form106Questions: 'S10',
  translateQuestions: 'S11',
  foreignConfirm: 'S12',
  translateConfirm: 'S13',
  payslipConfirm: 'S14',
  form106Confirm: 'S15',
  translateResults: 'S16',
  payslipResults: 'S17',
  foreignResults: 'S18',
  form106Results: 'S19',
  privacy: 'S20',
  disclaimer: 'S21',
  accessibility: 'S22',
  terms: 'S23',
  welcomeLanguage: 'S24',
  welcomeWhy: 'S25',
  welcomeTrust: 'S26',
  leave: 'S27',
  remove: 'S28',
  clear: 'S29',
  replace: 'S30',
  removeTyped: 'S31',
} as const;
export type ScreenKey = keyof typeof SCREEN_CODES;

/** What each screen is, in a few words (docs/review/screen-codes.md and the tester guide). */
export const SCREEN_NAMES: Record<ScreenKey, string> = {
  start: 'Start: what to check',
  who: 'Who are you',
  upload: 'Upload payslips',
  uploadForm106: 'Upload 106 forms',
  manualEntry: 'Type in a payslip (form open)',
  form106Entry: 'Type in a Form 106 (form open)',
  scanReview: 'A scan or photo being read or checked',
  payslipQuestions: 'Payslip: questions',
  payslipConfirm: 'Payslip: check your answers',
  payslipResults: 'Payslip: results',
  form106Questions: 'Form 106: questions',
  form106Confirm: 'Form 106: check your answers',
  form106Results: 'Form 106: results',
  translateQuestions: 'Translate a payslip: questions',
  translateConfirm: 'Translate a payslip: check your answers',
  translateResults: 'Translate a payslip: results with the translation (or any payslip translation open)',
  foreignQuestions: 'Foreign worker: questions',
  foreignConfirm: 'Foreign worker: check your answers',
  foreignResults: 'Foreign worker: results',
  privacy: 'Privacy page',
  disclaimer: 'Disclaimer page',
  accessibility: 'Accessibility statement',
  terms: 'Terms of use',
  welcomeLanguage: 'Welcome dialog, screen 1: language',
  welcomeWhy: 'Welcome dialog, screen 2: why',
  welcomeTrust: 'Welcome dialog, screen 3: privacy and limits',
  leave: 'Dialog: leave without saving',
  remove: 'Dialog: remove this file',
  clear: 'Dialog: clear every file',
  replace: 'Dialog: typed numbers replace the files',
  removeTyped: 'Dialog: remove the Form 106 numbers typed in',
};

/** The guided flow's steps (src/ui/app.ts `FlowStep`; the test keeps the two lists equal). */
export const FLOW_STEPS = ['goal', 'who', 'upload', 'questions', 'confirm', 'results'] as const;
export type FlowStep = (typeof FLOW_STEPS)[number];
/** The information pages (src/ui/pages.ts PAGE_NAMES). */
export const ABOUT_PAGES = { privacy: 'privacy', disclaimer: 'disclaimer', accessibility: 'accessibility', terms: 'terms' } as const satisfies Record<string, ScreenKey>;
/** The data-loss dialog's kinds (src/ui/leaveGuard.ts `LossKind`). */
export const LOSS_DIALOGS = { leave: 'leave', remove: 'remove', clear: 'clear', replace: 'replace', removeTyped: 'removeTyped' } as const satisfies Record<string, ScreenKey>;
/** The welcome dialog's screens, by index (src/ui/welcome.ts WELCOME_STEPS). */
export const WELCOME_SCREENS = ['welcomeLanguage', 'welcomeWhy', 'welcomeTrust'] as const satisfies readonly ScreenKey[];

/** Where the app is: the page shown, or the flow step and what decides its flow. */
export interface Where {
  page?: string;
  step: FlowStep;
  goal?: 'month' | 'several' | 'left' | 'form106' | undefined;
  /** "Who are you": a foreign worker (or the foreign-worker situation ticked). */
  foreign?: boolean;
  /** "Translate a payslip" chosen, or a payslip's translation open on the results. */
  translate?: boolean;
  /** Upload step: the payslip form open, the Form 106 form open, a scan being read or checked. */
  manualOpen?: boolean;
  form106Open?: boolean;
  scanReview?: boolean;
}

/** The screen the app shows. */
export function screenKey(w: Where): ScreenKey {
  if (w.page !== undefined) {
    const page = (ABOUT_PAGES as Record<string, ScreenKey>)[w.page];
    if (!page) throw new Error(`no screen code for the page "${w.page}" (src/beta/screenCodes.ts)`);
    return page;
  }
  const flow = w.goal === 'form106' ? 'form106' : w.translate ? 'translate' : w.foreign ? 'foreign' : 'payslip';
  switch (w.step) {
    case 'goal':
      return 'start';
    case 'who':
      return 'who';
    case 'upload':
      return w.scanReview ? 'scanReview' : w.form106Open ? 'form106Entry' : w.manualOpen ? 'manualEntry' : w.goal === 'form106' ? 'uploadForm106' : 'upload';
    case 'questions':
      return `${flow}Questions` as const;
    case 'confirm':
      return `${flow}Confirm` as const;
    case 'results':
      return `${flow}Results` as const;
    default: {
      const never: never = w.step;
      throw new Error(`no screen code for the step "${String(never)}" (src/beta/screenCodes.ts)`);
    }
  }
}

/** The state suffixes, by priority: ·e error, ·c can't check, ·m missing info, ·u uncertain, ·0 empty. */
export type Suffix = 'e' | 'c' | 'm' | 'u' | '0';
/**
 * What on the screen shows each state (CSS selectors on the drawn screen), first match wins. The screen's DOM is the
 * one truth that covers every flow alike (a payslip's and a Form 106's results draw the same cards).
 */
export const SUFFIX_SELECTORS: readonly (readonly [Suffix, string])[] = [
  ['e', '.errors, .fd-error, .file-item.status-scanFailed, .file-item.status-notRecognised, .reconcile-mismatch'],
  ['c', '.finding.cannot_check'],
  // A box asking for a value (not a "Change" box, which has Cancel), or a file row asking for its month.
  ['m', '.missing-info:not(:has([id$="-cancel"])), .file-item .missing-info'],
  ['u', '.ocr-note, .beta-vendor-note, .file-item.status-scanToRead, .file-item.status-scanToCheck, .file-item.status-doubtfulToRead, .file-item.status-locked, [id$="-unsure"], [id^="f106-review-"], .pension-checks'],
  ['0', '.empty-state, .card.upload:not(:has(.file-item))'],
];

/** The state suffix of a drawn screen. */
export function suffixOf(root: Pick<ParentNode, 'querySelector'>): Suffix | '' {
  for (const [suffix, selector] of SUFFIX_SELECTORS) if (root.querySelector(selector)) return suffix;
  return '';
}

/** "S17·c": the screen's code and its state. */
export const screenCode = (key: ScreenKey, suffix: Suffix | '' = ''): string => `${SCREEN_CODES[key]}${suffix ? `·${suffix}` : ''}`;

/** A code as shown: S and two digits, optional state. */
export const CODE_FORMAT = /^S\d{2}(·[ecmu0])?$/;
